import React, { useMemo, useState, useEffect } from 'react';
import { Card, Row, Col, Typography, DatePicker, Input, Space, Select } from 'antd';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import StandardTable from '../../../components/common/StandardTable';
import { useGetMatGapDeliveries } from '../../../hooks/genericHooks';
import type { MatGapDeliveries } from '../../../types';
import { formatNumberSmart } from '../../../utils/numberFormat';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export default function MatGapDeliveries() {
  const { data = [], isLoading } = useGetMatGapDeliveries() as any;
  const deliveries: MatGapDeliveries[] = Array.isArray(data) ? data : (data?.data ?? []);

  // Local UI filters
  const [search, setSearch] = useState<string>('');
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);
  const [shipmentSel, setShipmentSel] = useState<string>('');
  const [groupLongSel, setGroupLongSel] = useState<string>('');
  const [hierLongSel, setHierLongSel] = useState<string>('');
  const [extGroupSel, setExtGroupSel] = useState<string>('');

  const norm = (s: any) => (s == null ? '' : String(s)).trim();

  // Cascaded options
  const shipmentOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    (deliveries || [])
      .filter(r => !groupLongSel || norm(r.mat_group_long).toLowerCase() === norm(groupLongSel).toLowerCase())
      .filter(r => !hierLongSel || norm(r.hierarchy_long).toLowerCase() === norm(hierLongSel).toLowerCase())
      .filter(r => !extGroupSel || norm((r as any).ext_group).toLowerCase() === norm(extGroupSel).toLowerCase())
      .forEach(r => {
        const raw = r?.shipment_id == null ? '' : String((r as any).shipment_id);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [deliveries, groupLongSel, hierLongSel, extGroupSel]);

  const groupLongOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    (deliveries || [])
      .filter(r => !shipmentSel || String((r as any).shipment_id || '') === shipmentSel)
      .filter(r => !hierLongSel || norm(r.hierarchy_long).toLowerCase() === norm(hierLongSel).toLowerCase())
      .filter(r => !extGroupSel || norm((r as any).ext_group).toLowerCase() === norm(extGroupSel).toLowerCase())
      .forEach(r => {
        const raw = norm(r.mat_group_long);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [deliveries, shipmentSel, hierLongSel, extGroupSel]);

  const hierLongOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    (deliveries || [])
      .filter(r => !shipmentSel || String((r as any).shipment_id || '') === shipmentSel)
      .filter(r => !groupLongSel || norm(r.mat_group_long).toLowerCase() === norm(groupLongSel).toLowerCase())
      .filter(r => !extGroupSel || norm((r as any).ext_group).toLowerCase() === norm(extGroupSel).toLowerCase())
      .forEach(r => {
        const raw = norm(r.hierarchy_long);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [deliveries, shipmentSel, groupLongSel, extGroupSel]);

  const extGroupOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    (deliveries || [])
      .filter(r => !shipmentSel || String((r as any).shipment_id || '') === shipmentSel)
      .filter(r => !groupLongSel || norm(r.mat_group_long).toLowerCase() === norm(groupLongSel).toLowerCase())
      .filter(r => !hierLongSel || norm(r.hierarchy_long).toLowerCase() === norm(hierLongSel).toLowerCase())
      .forEach(r => {
        const raw = norm((r as any).ext_group);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [deliveries, shipmentSel, groupLongSel, hierLongSel]);

  const filteredData = useMemo(() => {
    const [start, end] = dateRange;
    const s = search.trim().toLowerCase();
    return (deliveries || []).filter((rec) => {
      // search against mat_id, mat_desc, sat_no, sas_no
      if (s) {
        const hay = [
          String(rec.mat_id || ''),
          String((rec as any).shipment_id || ''),
          rec.mat_desc || '',
          rec.sat_no || '',
          rec.sas_no || '',
          rec.shipped_from || '',
          rec.notes || '',
          rec.unit || '',
          rec.mat_group || '',
          rec.mat_group_long || '',
          rec.hierarchy || '',
          rec.hierarchy_long || '',
        ].join(' ').toLowerCase();
        if (!hay.includes(s)) return false;
      }
      // dropdown filters (exact match on selected values)
      if (shipmentSel && String((rec as any).shipment_id || '') !== shipmentSel) return false;
      if (groupLongSel && norm(rec.mat_group_long).toLowerCase() !== norm(groupLongSel).toLowerCase()) return false;
      if (hierLongSel && norm(rec.hierarchy_long).toLowerCase() !== norm(hierLongSel).toLowerCase()) return false;
      if (extGroupSel && norm((rec as any).ext_group).toLowerCase() !== norm(extGroupSel).toLowerCase()) return false;
      // date range filter (inclusive)
      if (start || end) {
        const d = rec.delivery_date ? new Date(rec.delivery_date) : null;
        if (!d) return false;
        if (start) {
          const ds = new Date(start);
          if (d < new Date(ds.getFullYear(), ds.getMonth(), ds.getDate())) return false;
        }
        if (end) {
          const de = new Date(end);
          if (d > new Date(de.getFullYear(), de.getMonth(), de.getDate(), 23, 59, 59, 999)) return false;
        }
      }
      return true;
    });
  }, [deliveries, search, dateRange, shipmentSel, groupLongSel, hierLongSel, extGroupSel]);

  // Auto-clear invalid selections when options narrow
  useEffect(() => {
    if (shipmentSel && !shipmentOptions.some(o => (o.value as string) === shipmentSel)) setShipmentSel('');
  }, [shipmentOptions, shipmentSel]);
  useEffect(() => {
    if (groupLongSel && !groupLongOptions.some(o => (o.value as string) === groupLongSel)) setGroupLongSel('');
  }, [groupLongOptions, groupLongSel]);
  useEffect(() => {
    if (hierLongSel && !hierLongOptions.some(o => (o.value as string) === hierLongSel)) setHierLongSel('');
  }, [hierLongOptions, hierLongSel]);
  useEffect(() => {
    if (extGroupSel && !extGroupOptions.some(o => (o.value as string) === extGroupSel)) setExtGroupSel('');
  }, [extGroupOptions, extGroupSel]);

  const columns: any[] = useMemo(() => [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80, sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => (a.id ?? 0) - (b.id ?? 0) },
    { title: 'Shipment ID', dataIndex: 'shipment_id', key: 'shipment_id', width: 140, sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => String(a.shipment_id || '').localeCompare(String(b.shipment_id || '')) },
    { title: 'Material ID', dataIndex: 'mat_id', key: 'mat_id', width: 140, sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => String(a.mat_id || '').localeCompare(String(b.mat_id || '')) },
    { title: 'Description', dataIndex: 'mat_desc', key: 'mat_desc', ellipsis: true, width: 260, sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => (a.mat_desc || '').localeCompare(b.mat_desc || '') },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 90, sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => (a.unit || '').localeCompare(b.unit || '') },
    { title: 'Group', dataIndex: 'mat_group', key: 'mat_group', width: 140, sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => (a.mat_group || '').localeCompare(b.mat_group || '') },
    { title: 'Group (Long)', dataIndex: 'mat_group_long', key: 'mat_group_long', width: 220, ellipsis: true, sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => (a.mat_group_long || '').localeCompare(b.mat_group_long || '') },
    { title: 'Hierarchy', dataIndex: 'hierarchy', key: 'hierarchy', width: 160, sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => (a.hierarchy || '').localeCompare(b.hierarchy || '') },
    { title: 'Hierarchy (Long)', dataIndex: 'hierarchy_long', key: 'hierarchy_long', width: 240, ellipsis: true, sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => (a.hierarchy_long || '').localeCompare(b.hierarchy_long || '') },
    { title: 'Ext Group', dataIndex: 'ext_group', key: 'ext_group', width: 160, sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => String((a as any).ext_group || '').localeCompare(String((b as any).ext_group || '')) },
    { title: 'Qty', dataIndex: 'qty', key: 'qty', width: 120, align: 'right', render: (v: unknown) => formatNumberSmart(v), sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => (a.qty ?? 0) - (b.qty ?? 0) },
    { title: 'Delivery Date', dataIndex: 'delivery_date', key: 'delivery_date', width: 160,
      render: (v: string) => v ? dayjs(v).format('YYYY-MM-DD') : '-',
      sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => new Date(a.delivery_date || 0).getTime() - new Date(b.delivery_date || 0).getTime(),
      exportValue: (r: MatGapDeliveries) => r.delivery_date ? dayjs(r.delivery_date).format('YYYY-MM-DD') : ''
    },
    { title: 'SAT No', dataIndex: 'sat_no', key: 'sat_no', width: 140, sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => (a.sat_no || '').localeCompare(b.sat_no || '') },
    { title: 'SAS No', dataIndex: 'sas_no', key: 'sas_no', width: 140, sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => (a.sas_no || '').localeCompare(b.sas_no || '') },
    { title: 'Shipped From', dataIndex: 'shipped_from', key: 'shipped_from', width: 160, ellipsis: true, sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => String(a.shipped_from || '').localeCompare(String(b.shipped_from || '')) },
    { title: 'Notes', dataIndex: 'notes', key: 'notes', width: 240, ellipsis: true, sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => String(a.notes || '').localeCompare(String(b.notes || '')) },
  ], []);

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>GAP Deliveries</Title>
          <Text type="secondary">List of material deliveries (mat_gap_deliveries)</Text>
        </Col>
        <Col>
          <Space size={8}>
            <Select
              placeholder="Shipment ID"
              allowClear
              showSearch
              size="small"
              value={shipmentSel || undefined}
              onChange={(v) => setShipmentSel(v || '')}
              options={shipmentOptions}
              style={{ width: 140 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
            />
            <Select
              placeholder="Group (Long)"
              allowClear
              showSearch
              size="small"
              value={groupLongSel || undefined}
              onChange={(v) => setGroupLongSel(v || '')}
              options={groupLongOptions}
              style={{ width: 180 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
            />
            <Select
              placeholder="Hierarchy (Long)"
              allowClear
              showSearch
              size="small"
              value={hierLongSel || undefined}
              onChange={(v) => setHierLongSel(v || '')}
              options={hierLongOptions}
              style={{ width: 180 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
            />
            <Select
              placeholder="Ext Group"
              allowClear
              showSearch
              size="small"
              value={extGroupSel || undefined}
              onChange={(v) => setExtGroupSel(v || '')}
              options={extGroupOptions}
              style={{ width: 160 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
            />
            <Input
              placeholder="Search material, desc, SAT/SAS..."
              allowClear
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 180 }}
            />
            <RangePicker
              size="small"
              style={{ width: 220 }}
              value={[
                dateRange[0] ? dayjs(dateRange[0]) : null,
                dateRange[1] ? dayjs(dateRange[1]) : null,
              ]}
              onChange={(vals) => {
                if (!vals || vals.length !== 2) { setDateRange([null, null]); return; }
                setDateRange([
                  vals[0] ? vals[0].format('YYYY-MM-DD') : null,
                  vals[1] ? vals[1].format('YYYY-MM-DD') : null,
                ]);
              }}
            />
            <Button
              size="small"
              onClick={() => {
                setShipmentSel('');
                setGroupLongSel('');
                setHierLongSel('');
                setExtGroupSel('');
                setSearch('');
                setDateRange([null, null]);
              }}
            >
              Reset Filters
            </Button>
          </Space>
        </Col>
      </Row>
      <StandardTable<MatGapDeliveries>
        columns={columns}
        dataSource={filteredData}
        rowKey={(r) => String(r.id)}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
}
